/**
 * Shared types for state management
 *
 * These types are used across both the dashboard (Epic 9) and game (Epic 10).
 */

import type { AreaOfNeed } from './areasOfNeeds';

// Proposal types
export type ProposalScale = 'small' | 'medium' | 'large';

// Areas of Needs (story: proposal-vertical-taxonomy-reconcile) — the canonical
// "seven sectors of human need" axis, multi-select. Value set + labels + framing
// copy live in ONE place; re-exported here so `@/stores` consumers get them.
// This REPLACES the old single-select `ProposalVertical` union (Housing/Food/
// Energy/Education/Community/Infrastructure/Other), which was the wrong 7 items.
export * from './areasOfNeeds';

// Proposal-AI output types (tt-rm-14.3)
//
// The proposal-AI now runs on the FounderyOS core BFF
// (`POST /api/v1/governance/proposals/draft`), which returns the locked
// `ProposalDraft` shape (tt-rm-14.1/14.2). This REPLACES the old
// `ThinkTankOutput` (problemStatement / budgetBreakdown / timeline / ...).
// The structured output stays client-side (localStorage / atoms) — only the
// final user-edited description text is ever written on-chain (D1).

/** A single free-form section of a generated proposal draft. */
export interface ProposalDraftSection {
  heading: string;
  content: string;
}

/**
 * The proposal-AI draft output — the FounderyOS core BFF contract shape.
 * `{ title, summary, body, sections[], suggested_tags[] }`.
 */
export interface ProposalDraftOutput {
  title: string;
  summary: string;
  body: string;
  sections: ProposalDraftSection[];
  suggested_tags: string[];
}

/**
 * Top-level (non-section) editable fields of a `ProposalDraftOutput`.
 * Used alongside section headings to track which parts the member edited.
 */
export type ProposalDraftField = 'title' | 'summary' | 'body';

// Vote types
//
// bl-1172: `needs_refinement` is the fourth governance ballot. The canister has
// always accepted `VoteChoice::NeedsRefinement` (governance/src/governance.did:82)
// and oracle-bridge carries it on the wire (castGovernanceVoteSchema); this union
// is the last place it was unrepresentable. NeedsRefinement counts toward QUORUM
// but NOT toward APPROVAL, so it is not interchangeable with any other choice.
export type VoteChoice = 'yes' | 'no' | 'abstain' | 'needs_refinement';

/**
 * A proposal's vote tally as the governance canister reports it.
 *
 * bl-1088-1: every field here is SERVER-DERIVED. The client carries no quorum or
 * approval threshold of its own — they are snapshotted onto each proposal at
 * creation (`Proposal.quorum_threshold` / `approval_threshold`) and vary by
 * category and tier, so a client-side copy would eventually contradict the chain
 * and tell a member a passing proposal was failing.
 */
export interface VoteTally {
  yes: number;
  no: number;
  abstain: number;
  /**
   * `NeedsRefinement` ballots. These count toward QUORUM but NOT toward approval
   * (governance/src/tally.rs) — which is why `totalVotes` cannot be reconciled
   * from yes/no/abstain alone and this field has to be carried.
   */
  needsRefinement: number;
  /** Server-supplied `total_votes` (yes + no + abstain + needsRefinement). */
  totalVotes: number;
  /** The proposal's own `quorum_threshold`. Never a client constant. */
  quorumRequired: number;
  /** Canister-computed. Never recomputed client-side. */
  quorumMet: boolean;
  /** The proposal's own `approval_threshold`, 0-100. Never a client constant. */
  passingThreshold: number;
  /** Canister-computed `approval_percentage`, 0-100. Never recomputed client-side. */
  approvalPercentage: number;
  /** Canister-computed. Never recomputed client-side. */
  approvalMet: boolean;
  lastUpdated: number; // Timestamp for "Last updated X ago"
}

export interface UserVote {
  proposalId: string;
  vote: VoteChoice;
  /**
   * When the ballot was recorded.
   *
   * bl-1088-1: this is the canister's `voted_at` when the ballot was read back
   * from the server, and a LOCAL DISPLAY TIMESTAMP when it was set immediately
   * after a successful cast that the server-re-read could not confirm in time.
   * It must never be labelled or rendered as an on-chain time.
   */
  votedAt: number;
  /**
   * On-chain proof for a vote receipt, when one exists.
   *
   * bl-1088-1: oracle-bridge's `POST /proposals/:id/vote` issues NO receipt — it
   * responds `{success:true}` and nothing more. This field is therefore
   * `undefined` for governance votes and MUST NOT be filled with a
   * client-minted value to satisfy the type.
   */
  transactionId?: string;
}

export interface PendingVote {
  proposalId: string;
  vote: VoteChoice;
  startedAt: number; // When submission started
}

// Proposal List types (Story 9-1-3)
export type ProposalStatus = 'Active' | 'Passed' | 'Failed' | 'Pending';
export type ProposalSort = 'newest' | 'oldest' | 'mostVotes' | 'endingSoon';

/**
 * BL-189 / PLATFORM-004.1: A single entry in a proposal's edit history.
 *
 * When `update_proposal` is called on the governance canister the PREVIOUS
 * description is appended to the proposal's `versions` list as a
 * `ProposalVersion` entry. The CURRENT description stays on the proposal
 * itself (at `versionNumber`); entries here are strictly previous versions.
 *
 * Timestamps are nanoseconds-since-epoch to match the canister wire format
 * (consumers that need ms should divide by 1_000_000).
 */
export interface ProposalVersion {
  /** 1-indexed version number captured by this entry. */
  versionNumber: number;
  /** The proposal description text at this version. */
  content: string;
  /**
   * Nanoseconds-since-epoch timestamp when this version was superseded.
   *
   * BL-222: carried as a STRING, not a number. The canister stores this as a
   * `Nat64` (nanoseconds); realistic values (~1.75e18) exceed
   * `Number.MAX_SAFE_INTEGER` (~9.007e15), so a `Number` parse silently loses
   * precision. Kept as the raw digit string to match the other Nat64 wire
   * timestamps (`createdAtNs`, `updatedAtNs`, …). Divide via BigInt for ms.
   */
  updatedAt: string;
  /** IC principal (as string) of the member that performed the update. */
  updatedBy: string;
}

/**
 * BL-189: Proposal list row returned by oracle-bridge's list endpoint.
 *
 * `versionNumber` + `hasVersions` are optional — older proposals created
 * before PLATFORM-004.1 landed don't have edit history, so consumers must
 * treat `undefined` as "original, never edited". `hasVersions` is a cheap
 * boolean flag the list endpoint can populate without inlining the full
 * history; full history is fetched via a dedicated versions endpoint.
 *
 * BL-227: `_ns` timestamp fields added for sub-ms ordering precision.
 * These are 19-digit nanosecond-epoch strings emitted by oracle-bridge
 * (BL-222 dual-field timestamps). Ordering consumers use BigInt(_ns) with
 * a ms-field fallback; display consumers keep using the ms fields.
 */
export interface ProposalListItem {
  id: string;
  title: string;
  proposer: string; // Principal ID of proposal creator
  status: ProposalStatus;
  votesFor: number;
  votesAgainst: number;
  votesAbstain: number;
  votingEndsAt: number;
  createdAt: number;
  /** 1-indexed current version number (BL-189 / PLATFORM-004.1). */
  versionNumber?: number;
  /** True when the proposal has been edited at least once. */
  hasVersions?: boolean;
  // BL-227: nanosecond-precision timestamps for ordering (optional — older
  // oracle-bridge versions omit these; fall back to ms fields when absent).
  /** Nanoseconds-since-epoch string for creation time (BL-227). */
  createdAtNs?: string;
  /** Nanoseconds-since-epoch string for voting start time (BL-227). */
  votingStartAtNs?: string;
  /** Nanoseconds-since-epoch string for voting end time (BL-227). */
  votingEndsAtNs?: string;
  /**
   * org-002-1 / org-006-1: Org-scope of this proposal — three-state semantic:
   *   - `undefined` → field absent on the wire (older oracle-bridge build);
   *                   render NO badge (graceful degradation)
   *   - `null`      → Proposal.org_id was None on the canister → HWDAO-wide
   *   - `number`    → Proposal.org_id was Some(id) → scoped to that Official Org
   * Phase 1 renders `Org {id}` literally; Phase 2 resolves to org display name.
   */
  orgId?: number | null;
}

/**
 * org-002-3 / org-006-1: read-side scope filter for the proposal list. TS
 * discriminated union matching the governance canister's Candid `OrgScope`
 * variant shape — do NOT collapse to `'all' | 'helloworld' | { type: 'org', id }`
 * (R8 invariant from canister, carried through to the bridge transformer).
 *
 *   - `{ All: null }`        → no filter on org_id (HWDAO + every Official Org)
 *   - `{ HelloWorld: null }` → only proposals where org_id is None (HWDAO-wide)
 *   - `{ Org: bigint }`      → only proposals where org_id == Some(id);
 *                              `bigint` because canister-side OrgId = u64
 *                              exceeds Number.MAX_SAFE_INTEGER
 *
 * Semantic identity: `undefined` (param omitted) ≡ `{ All: null }` — both
 * result in NO `org_scope` HTTP query param being sent to oracle-bridge.
 */
export type OrgScope =
  | { All: null }
  | { HelloWorld: null }
  | { Org: bigint };

export interface ProposalFilters {
  status: ProposalStatus[];
  search: string;
  myProposals: boolean; // Filter to proposals created by current user
  notVoted: boolean; // Filter to proposals user hasn't voted on
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  statusCounts?: ProposalStatusCounts;
}

export interface ProposalStatusCounts {
  Active: number;
  Passed: number;
  Failed: number;
  Pending: number;
}

// Draft status
export type DraftStatus = 'drafting' | 'ai-processing' | 'ready-for-review';

// Draft sort options
export type DraftSort = 'recent' | 'title';

// Current schema version for draft migrations.
// Bumped to 2 in tt-rm-14.3: the proposal-AI output shape changed from the old
// `thinkTankOutput` (problemStatement/budget/timeline/...) to `aiDraft`
// (`ProposalDraftOutput` = title/summary/body/sections/suggested_tags) and
// `thinkTankRequestId` was dropped (the FOS core contract is synchronous).
// Bumped to 3 in proposal-vertical-taxonomy-reconcile: the single-select
// `vertical: ProposalVertical` field became a multi-select `areasOfNeeds:
// AreaOfNeed[]`. A pre-v3 draft's legacy single `vertical` string is migrated to
// a canonical array (see `migrateLegacyVertical` / `drafts.ts`).
export const DRAFT_SCHEMA_VERSION = 3;

// Maximum number of drafts allowed per user
export const MAX_DRAFTS = 20;

// Proposal draft
export interface ProposalDraft {
  id: string;
  title: string;
  prompt: string;
  scale: ProposalScale;
  /**
   * The areas of needs this proposal serves (multi-select, one or several) —
   * proposal-vertical-taxonomy-reconcile. Replaces the single `vertical` field.
   * ≥2 selected ⇒ an EMERGENT hub/campus rollup (see `isCampusRollup`).
   * Legacy single-`vertical` drafts are migrated to a one-element (or empty)
   * array by `migrateDraftSchema` (drafts.ts).
   */
  areasOfNeeds: AreaOfNeed[];
  /** Proposal-AI draft output (tt-rm-14.3 rename of `thinkTankOutput`). */
  aiDraft?: ProposalDraftOutput;
  createdAt: number;
  updatedAt: number;
  status: DraftStatus;
  /**
   * Which parts of `aiDraft` the member manually edited. Entries are either a
   * top-level field name (`'title' | 'summary' | 'body'`) or a section heading.
   */
  editedSections: string[];
  // Story 9-1-6: New fields for draft management
  currentStep: number;       // Wizard step for resume (0-indexed)
  userPrincipal: string;     // User scoping for privacy
  schemaVersion: number;     // For future migrations
  /**
   * org-006-3: Submit-on-behalf-of-org selection. Stashed by `StepReview`
   * via `<SubmitOnBehalfSelector />` so it survives the `StepReview` →
   * `SubmitConfirmation` navigation. Three-state semantic mirrors
   * `SubmitProposalRequest.orgId`:
   *   - `undefined` → never picked (older drafts; canister-side default = HWDAO)
   *   - `null`      → explicit HWDAO
   *   - `number`    → org-scoped submit
   * Backward-compatible additive field — existing drafts in the store with
   * no `orgId` field deserialize as `undefined` and submit as HWDAO.
   */
  orgId?: number | null;
}
